import isEmpty from 'lodash/fp/isEmpty';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import AutoPlayVideo from 'Components/AutoPlayVideo';
import { Image } from 'Components/Image';
import { Button } from 'Components/LegacyButton';
import TwoUp from 'Components/LegacyTwoUp';
import { NoListChecked } from 'Components/NoListChecked';
import { Typography } from 'Components/Typography';

import BlockLayout from 'Blocks/BlockLayout';

import { srcOrNextImageProp } from 'PropTypes/imageProps';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const MediaWrapper = styled.div`
  position: relative;
  overflow: hidden;

  width: 100%;
  height: 66.67vw;

  ${legacyTheme.breakpoints.up('md')} {
    height: 100%;
  }

  img {
    width: 100%;
    object-fit: cover;
  }

  video {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);

    width: 100%;
    min-height: 100.5%;

    object-fit: cover;
  }
`;

const Title = styled(Typography)`
  padding-right: ${legacyTheme.spacing.s16};

  line-height: 40px;
  font-size: 24px;

  ${legacyTheme.breakpoints.up('sm')} {
    font-size: 32px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    padding-right: 64px;
  }
`;

type NoListBlockProps = {
  content: {
    cta: {
      label: string;
      link: string;
    };
    description: string;
    image: {
      alt: string;
      imageDesktop: typeof srcOrNextImageProp;
      imageMobile: typeof srcOrNextImageProp;
    };
    list: string[];
    title: string;
    video?: {
      mobile: string;
      desktop: string;
    };
  };
};

export const NoListBlock = ({ content }: NoListBlockProps) => {
  const { cta, description, image, list, title, video } = content;
  const { isMobile } = useResponsiveVariant();

  const hasVideoUrl = !isEmpty(content?.video);
  const imageSrc = isMobile ? image.imageMobile : image.imageDesktop;

  return (
    // @ts-expect-error - TwoUp has no TS types yet
    <TwoUp
      backgroundColor={theme.colors.neutral[100]}
      data-testid="product-nolist-block"
      wideScreenOrder="12"
    >
      {
        <MediaWrapper>
          {hasVideoUrl ? (
            // @ts-expect-error - AutoPlayVideo has no TS types yet
            <AutoPlayVideo
              desktopImage={image?.imageDesktop}
              desktopVideo={video?.desktop}
              imgAlt={image.alt}
              mobileImage={image?.imageMobile}
              mobileVideo={video?.mobile}
              width="100%"
            />
          ) : (
            // @ts-expect-error -(@pierre)  TODO: fix src type error
            <Image alt={image.alt} fill src={imageSrc} />
          )}
        </MediaWrapper>
      }
      {/* @ts-expect-error - BlockLayout has no TS types yet */}
      <BlockLayout
        blockLayoutMargin={`${isMobile ? 72 : 90}px auto`}
        textPadding={isMobile ? 32 : 96}
      >
        <Title color="vert" markupName="h2" paragraph variant="h3">
          {title}
        </Title>

        <Typography color="vert" paragraph variant="p1">
          {description}
        </Typography>
        <NoListChecked list={list} />
        <Button
          Component="a"
          data-click="click-ingredients"
          data-from="never-list"
          href={cta.link}
          marginTop
          rel="noopener noreferrer"
          target="_blank"
          variant="underlined"
        >
          {cta.label}
        </Button>
      </BlockLayout>
    </TwoUp>
  );
};
